import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import {
  plansApi,
  type CreateSubscriptionPlanRequest,
  type UpdateSubscriptionPlanRequest,
} from '@/lib/api/plans';
import type { ApiError } from '@/lib/api/client';
import { toast } from 'sonner';

/** Extract a human-readable message from an axios/unknown error. */
function errorMessage(error: unknown, fallback: string): string {
  if (isAxiosError<ApiError>(error)) {
    return error.response?.data?.message || error.message || fallback;
  }
  if (error instanceof Error) return error.message || fallback;
  return fallback;
}

/**
 * Fetch a paginated list of subscription plans.
 */
export function usePlans(page = 1, limit = 20) {
  return useQuery({
    queryKey: ['plans', page, limit],
    queryFn: () => plansApi.getPlans(page, limit),
  });
}

/**
 * Fetch a single plan by ID.
 */
export function usePlan(id: string | null) {
  return useQuery({
    queryKey: ['plan', id],
    queryFn: () => plansApi.getPlan(id!),
    enabled: !!id,
  });
}

/**
 * Create a new plan.
 */
export function useCreatePlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateSubscriptionPlanRequest) => plansApi.createPlan(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plans'] });
      toast.success('Plan created successfully');
    },
    onError: (error: unknown) => {
      toast.error(errorMessage(error, 'Failed to create plan'));
    },
  });
}

/**
 * Update an existing plan.
 */
export function useUpdatePlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateSubscriptionPlanRequest }) =>
      plansApi.updatePlan(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['plans'] });
      queryClient.invalidateQueries({ queryKey: ['plan', variables.id] });
      toast.success('Plan updated successfully');
    },
    onError: (error: unknown) => {
      toast.error(errorMessage(error, 'Failed to update plan'));
    },
  });
}

/**
 * Move a plan's active subscribers to another plan. Separate from deletion on
 * purpose: it is a real billing change, made and confirmed on its own.
 */
export function useMoveSubscribers() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, toPlanId }: { id: string; toPlanId: string }) => plansApi.moveSubscribers(id, toPlanId),
    onSuccess: (moved) => {
      queryClient.invalidateQueries({ queryKey: ['plans'] });
      toast.success(`Moved ${moved} subscriber${moved === 1 ? '' : 's'}`);
    },
    onError: (error: unknown) => {
      toast.error(errorMessage(error, 'Failed to move subscribers'));
    },
  });
}

/**
 * Delete a plan.
 */
export function useDeletePlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => plansApi.deletePlan(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plans'] });
      toast.success('Plan deleted successfully');
    },
    onError: (error: unknown) => {
      toast.error(errorMessage(error, 'Failed to delete plan'));
    },
  });
}
